import { ArrowDownRight, ArrowUpRight, Expand, Minus } from 'lucide-react';
import { hapticLight } from '../../lib/haptics';

export function formatCurrencyCompact(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value || 0);
}

export function formatDays(days: number): string {
  if (days <= 0) return '—';
  if (days < 10) return `${days.toFixed(1)} days`;
  return `${Math.round(days)} days`;
}

/** Human-friendly duration: minutes under an hour, hours under a day, else days. */
export function formatDuration(days: number): string {
  if (days <= 0) return '—';
  const hours = days * 24;
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 24) return hours < 10 ? `${hours.toFixed(1)} hrs` : `${Math.round(hours)} hrs`;
  return days < 10 ? `${days.toFixed(1)} days` : `${Math.round(days)} days`;
}

export type Tone = 'up' | 'down' | 'flat';

export interface Trend {
  text: string;
  tone: Tone;
}

/** Relative % change as a compact chip value (e.g. "+12%"), sentiment-aware. */
export function relativeTrend(
  current: number,
  previous: number,
  options?: { invert?: boolean }
): Trend | null {
  const invert = options?.invert ?? false;
  if (previous === 0) {
    if (current > 0) return { text: 'New', tone: invert ? 'down' : 'up' };
    return null;
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return { text: '0%', tone: 'flat' };
  const natural: Tone = pct > 0 ? 'up' : 'down';
  const tone: Tone = invert ? (natural === 'up' ? 'down' : 'up') : natural;
  return { text: `${pct > 0 ? '+' : ''}${pct}%`, tone };
}

export function TrendChip({ trend }: { trend: Trend | null }) {
  if (!trend) return null;
  const toneClass =
    trend.tone === 'up'
      ? 'bg-emerald-50 text-emerald-700'
      : trend.tone === 'down'
        ? 'bg-red-50 text-red-700'
        : 'bg-gray-100 text-gray-500';
  const Icon = trend.tone === 'up' ? ArrowUpRight : trend.tone === 'down' ? ArrowDownRight : Minus;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-0.5 rounded-full px-2 py-1 text-[11px] font-semibold leading-none ${toneClass}`}
    >
      <Icon className="h-3 w-3" strokeWidth={2.5} />
      {trend.text}
    </span>
  );
}

export interface DonutSegment {
  value: number;
  color: string;
}

interface DonutGaugeProps {
  segments: DonutSegment[];
  /** Denominator for the arcs. Defaults to the sum of segment values; pass 100 for a % gauge. */
  total?: number;
  size?: number;
  stroke?: number;
  centerValue: string;
  centerLabel: string;
  /** Index of the currently active (hovered/tapped) segment, for highlighting. */
  activeIndex?: number | null;
  /** When provided, segments become hover/tap interactive (passes index or null). */
  onSegmentActivate?: (index: number | null) => void;
}

/** Generic SVG ring/donut: a track plus one or more value arcs, with center text. */
export function DonutGauge({
  segments,
  total,
  size = 128,
  stroke = 14,
  centerValue,
  centerLabel,
  activeIndex = null,
  onSegmentActivate,
}: DonutGaugeProps) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const whole = total ?? segments.reduce((sum, s) => sum + s.value, 0);
  const cap = segments.length <= 1 ? 'round' : 'butt';
  const interactive = Boolean(onSegmentActivate);

  let offset = 0;
  const arcs =
    whole > 0
      ? segments.map((seg, index) => {
          const len = Math.max(0, (seg.value / whole) * circumference);
          const dash = `${len} ${circumference - len}`;
          const dimmed = activeIndex != null && activeIndex !== index;
          const startOffset = offset;
          offset += len;
          return (
            <circle
              key={index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={stroke}
              strokeLinecap={cap}
              strokeDasharray={dash}
              strokeDashoffset={-startOffset}
              style={{
                opacity: dimmed ? 0.35 : 1,
                transition: 'opacity 150ms',
                cursor: interactive ? 'pointer' : undefined,
              }}
              onPointerDown={
                interactive
                  ? () => onSegmentActivate?.(activeIndex === index ? null : index)
                  : undefined
              }
              onMouseEnter={interactive ? () => onSegmentActivate?.(index) : undefined}
              onMouseLeave={interactive ? () => onSegmentActivate?.(null) : undefined}
            />
          );
        })
      : null;

  return (
    <div className="relative" style={{ width: size, maxWidth: '100%' }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="h-auto w-full -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
        {arcs}
      </svg>
      {/* pointer-events-none so taps/hovers pass through to the arcs beneath */}
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-3 text-center">
        <span
          className="text-[24px] font-bold leading-none text-gray-900 tabular-nums"
          style={{ letterSpacing: '-0.04em' }}
        >
          {centerValue}
        </span>
        <span className="mt-1 max-w-full truncate text-[10px] font-semibold uppercase tracking-[0.06em] text-gray-400">
          {centerLabel}
        </span>
      </div>
    </div>
  );
}

/** Small "⤢ By month" link used under the ring gauges. */
export function ByMonthButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={() => {
        void hapticLight();
        onClick();
      }}
      aria-label={label}
      className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 active:scale-95 touch-manipulation"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <Expand className="h-3 w-3" strokeWidth={2.25} />
      By month
    </button>
  );
}
